import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  InputLabel,
} from "@mui/material";
import { useAppDispatch } from "shared/config/configureStore";
import { useForm, Controller } from "react-hook-form";
import { useAppSelector } from "shared/lib/hooks/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { createProductCategory } from "api/thunk/productCategories/postActions/createProductCategory";
import { useEffect } from "react";
import { getProductCategoryByCategoryId } from "api/thunk/productCategories/getActions/getProductCategoryByCategoryId";
import { updateProductCategory } from "api/thunk/productCategories/putActions/updateProductCategory";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

type FormValues = {
  name: string;
};

const fieldStyle = {
  width: "100%",
  height: 40,
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": { borderColor: "#C0D4E3", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#0090D1" },
    "&.Mui-focused fieldset": { borderColor: "#0090D1" },
  },
};

const CreateCategory = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: productCategoryById, loading: productCategoryByIdLoading } = useAppSelector((state) => state.productsCategories.productCategoryById.response);
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      name: "",
    },
  });

  const { loading } = useAppSelector(
    (state) => state.productsCategories.productCategory.response
  );

  const clearForm = () => {
    reset({
      name: "",
    })
  };

  const onSubmit = (data: FormValues) => {
    if (id) {
      dispatch(updateProductCategory({
        categoryId: Number(id),
        name: data.name
      })).unwrap()
        .then(() => {
          toast.success(String(t("toast.productCategoryUpdatedSuccessfully")))
          clearForm()
          navigate("/admin_panel/marketplace?tab=categories")
        })
        .catch((error) => console.log(error));
    } else {
      dispatch(createProductCategory({ name: data.name })).unwrap()
        .then(() => {
          toast.success(String(t("toast.productCategoryCreatedSuccessfully")))
          clearForm()
          navigate("/admin_panel/marketplace?tab=categories")
        })
        .catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getProductCategoryByCategoryId(Number(id)))
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && productCategoryById) {
      reset({
        name: productCategoryById.name,
      });
    } else if (!id) {
      clearForm()
    }
  }, [productCategoryById, reset, id]);

  return (
    <Box position="relative">
      {(loading || productCategoryByIdLoading) && (
        <Box
        >
          <CircularProgress
            size={28}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }} />
        </Box>
      )}

      <Paper
        sx={{
          background: "#FFFFFF",
          borderRadius: "16px",
          p: 5,
          mt: 4,
          mx: "auto",
          maxWidth: 1140,
          width: "80%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={4}>
          {id ? t("marketplace.updateCategory") : t("marketplace.createCategory")}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={4}>
            <InputLabel sx={{ mb: 1, mt: 2 }}>{t("marketplace.categoryName")}</InputLabel>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Category name is required" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  placeholder={t("marketplace.categoryName")}
                  sx={fieldStyle}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                />
              )}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: "12px",
                px: 5,
                py: 1.5,
                mt: '20px',
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {id ? t("marketplace.updateCategory") : t("marketplace.createCategory")}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateCategory;
